import React from 'react';
import { useState, useMemo } from 'react';

import { useTranslation } from "react-i18next";

import { IconButton, useTheme, Stack, Divider, Paper } from '@mui/material';
import { DeleteForever } from '@mui/icons-material';

import { WithOptionsWidgetProps } from 'components/widgetParts/withOptionsWidget'
import SimpleFormDialog from "components/dialogs/SimpleFormDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import ImagePreview from './ImagePreview';
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsSingleSelect from 'components/settingsConfig/uiComponents/SettingsSingleSelect';
import SettingsColorPicker from 'components/settingsConfig/uiComponents/SettingsColorPicker';
import { OptionsValueType } from 'components/settingsConfig/uiComponents/SettingsSingleSelect';

export type ImageSettingsStateType = {
  'imageInfo': {},
  'imageBase64': string;
  'imageSize': string;
  'imagePosition': string;
  'backgroundColor'?: string;
};

export const defaultSettings: ImageSettingsStateType = {
  'imageInfo': {},
  'imageBase64': '',
  'imageSize': 'contain',
  'imagePosition': 'center',
  'backgroundColor': ''
};

export type ImageInfoType = {
  url?: string;
  name: string;
  size: number;
}

function ImageInfo(props: {
  imageInfo: ImageInfoType,
  handleSavedImageInfo: (value: null) => void,
  updateImageBase64: (imageData: string | null) => void,
}) {
  const theme = useTheme();
  const imageSize = Math.round(props.imageInfo.size / 1000);

  return (
    <>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          borderRadius: theme.general.borderRadius,
          pl: 1,
          backgroundColor: theme.colors.primary.lighter,
          color: theme.palette.common.black
        }}>
        <Stack direction="column" sx={{
          padding: 0.5
        }}>
          <strong style={{ wordBreak: "break-all" }}>{props.imageInfo?.name}</strong>
          <span>{imageSize} KB</span>
        </Stack>
        <IconButton
          sx={{ ml: "auto" }}
          onClick={() => {
            props.handleSavedImageInfo(null);
            props.updateImageBase64(null);
          }}
        >
          <DeleteForever sx={{ color: 'red' }} />
        </IconButton>
      </Stack>
      <Divider />
    </>
  );
}

export default function ImageSettings(props: WithOptionsWidgetProps) {
  const { i18n } = useTranslation();
  const widgetRef = { width: props.contentRef?.current.clientWidth, height: props.contentRef?.current.clientHeight };

  const [imageBase64, setImageBase64] = useState<string | ArrayBuffer | null>(props.widget?.data?.imageBase64 || '');
  const [savedImageInfo, setSavedImageInfo] = useState<ImageInfoType | null>(props.widget?.data?.savedImageInfo || null);
  const [settingsState, setSettingsState] = useState<ImageSettingsStateType>(props?.widget?.data?.settings ?? defaultSettings);

  const settingsOnChange = (updatedState: ImageSettingsStateType) => setSettingsState(updatedState);
  const handleSavedImageInfo = (imageData: ImageInfoType | null) => setSavedImageInfo(imageData);
  const updateImageBase64 = (value: string | ArrayBuffer | null) => setImageBase64(value);

  const imageSizeOptions: OptionsValueType[] = useMemo(() => (
    [
      {
        label: i18n.t("widget-types.image.settings.image-size-cover"),
        value: 'cover',
        tooltip: i18n.t("widget-types.image.settings.cover-tooltip")
      },
      {
        label: i18n.t("widget-types.image.settings.image-size-contain"),
        value: 'contain',
        tooltip: i18n.t("widget-types.image.settings.contain-tooltip")
      },
      {
        label: i18n.t("widget-types.image.settings.image-size-auto"),
        value: 'auto',
        tooltip: i18n.t("widget-types.image.settings.auto-tooltip")
      },
    ]
  ), []);

  const imagePositionOptions: OptionsValueType[] = useMemo(() => (
    [
      { label: i18n.t("center"), value: 'center' },
      { label: i18n.t("top"), value: 'top' },
      { label: i18n.t("right"), value: 'right' },
      { label: i18n.t("bottom"), value: 'bottom' },
      { label: i18n.t("left"), value: 'left' },
    ]
  ), []);

  let updateWidgetData = () => {
    if (props.widget?.id && props.onUpdateWidgetData) {
      props.onUpdateWidgetData(props.widget?.id, {
        savedImageInfo,
        imageBase64,
        settings: settingsState
      }).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh()
        }
      })
    }
  }

  return (
    <SimpleFormDialog
      fullWidth={true}
      maxWidth="md"
      isOpen={!!props.settingsDialogIsOpen}
      onCancel={() => {
        props.setSettingsDialogIsOpen(false)
      }}
      onConfirm={() => {
        updateWidgetData()
        props.setSettingsDialogIsOpen(false)
      }}
      title={i18n.t('widget-types.image.settings.title')}>
      <Paper sx={{ p: 2, pt: 0.5 }}>
      <SplitSettingsWrapper>
        <ImagePreview
          widgetRef={widgetRef}
          savedImageInfo={savedImageInfo}
          handleSavedImageInfo={handleSavedImageInfo}
          settingsState={settingsState}
          imageBase64={imageBase64}
          updateImageBase64={updateImageBase64} />

        <SettingsOptions<ImageSettingsStateType> settingsState={settingsState} onChange={settingsOnChange}>

          {savedImageInfo &&
            <ImageInfo
              imageInfo={savedImageInfo || null}
              handleSavedImageInfo={handleSavedImageInfo}
              updateImageBase64={updateImageBase64}
              />}

          <SettingsSingleSelect
            keyid='imageSize'
            label={i18n.t("widget-types.image.settings.image-size")}
            options={imageSizeOptions}
            defaultValue={imageSizeOptions.find((item: OptionsValueType) => (
              item.value === 'cover'
            )) ?? imageSizeOptions[0]}
            value={savedImageInfo === null
              ? undefined
              : imageSizeOptions.find((item: OptionsValueType) => (
                item.value === settingsState.imageSize
            ))} />

          <SettingsSingleSelect
            keyid='imagePosition'
            label={i18n.t("widget-types.image.settings.image-position")}
            options={imagePositionOptions}
            defaultValue={imagePositionOptions.find((item: OptionsValueType) => (
              item.value === 'center'
            )) ?? imagePositionOptions[0]}
            value={savedImageInfo === null
              ? undefined
              : imagePositionOptions.find((item: OptionsValueType) => (
                item.value === settingsState.imagePosition
            ))} />

          <SettingsColorPicker
            keyid='backgroundColor'
            label={i18n.t("background-color")}
            color={settingsState.backgroundColor} />

        </SettingsOptions>
      </SplitSettingsWrapper>
      </Paper>
    </SimpleFormDialog>
  )
}
